import { motion, useInView } from "framer-motion";
import { Check, CheckCheck } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";

export const TransportMessageSection = () => {
  const [visibleMessages, setVisibleMessages] = useState(0);
  const sectionRef = useRef<HTMLDivElement>(null);
  const isInView = useInView(sectionRef, { once: true, margin: "-100px" });

  const messages = useMemo(
    () => [
      {
        text: "Seu veículo foi coletado",
        time: "14:30",
        status: "delivered",
        delay: 1000,
      },
      {
        text: "Seu veículo chegou na Base São Paulo",
        time: "16:45",
        status: "delivered",
        delay: 2500,
      },
      {
        text: "Previsão de embarque em 2 horas",
        time: "18:20",
        status: "delivered",
        delay: 4000,
      },
      {
        text: "Seu veículo foi embarcado",
        time: "20:15",
        status: "delivered",
        delay: 3000,
      },
      {
        text: "Previsão de chegada: amanhã às 14h",
        time: "20:16",
        status: "sent",
        delay: 2500,
      },
    ],
    [],
  );

  useEffect(() => {
    if (!isInView) return;
    const timer = setTimeout(() => {
      if (visibleMessages < messages.length) {
        setVisibleMessages((prev) => prev + 1);
      }
    }, messages[visibleMessages]?.delay || 1000);

    return () => clearTimeout(timer);
  }, [visibleMessages, messages, isInView]);

  // Reset animation every 15 seconds
  useEffect(() => {
    if (!isInView) return;
    const resetTimer = setInterval(() => {
      setVisibleMessages(0);
    }, 15000);

    return () => clearInterval(resetTimer);
  }, [isInView]);

  return (
    <section
      ref={sectionRef}
      className="relative p-2 bg-[url('/images/caminhao.jpg')] bg-cover bg-center overflow-hidden 2xl:max-h-[700px] 2xl:min-h-[700px] max-h-[600px] min-h-[600px] flex flex-col justify-between"
    >
      <div className="absolute inset-0 bg-black/60 z-0" />
      <motion.div
        className="z-10"
        initial={{ x: -100, opacity: 0 }}
        animate={isInView ? { x: 0, opacity: 1 } : {}}
        transition={{ duration: 0.8, ease: "easeOut" }}
      >
        <h1
          className=" text-n-secondary 2xl:text-[100px] xl:text-[80px] lg:text-[60px] md:text-[50px] text-[30px]"
          style={{ fontFamily: "'Anton', sans-serif" }}
        >
          Uma forma ágil e inovadora
        </h1>
      </motion.div>

      <div className="flex items-center justify-end absolute right-0 mr-10 z-10">
        <div className="w-full max-w-sm 2xl:space-y-3 space-y-2 mt-20 ">
          {messages.slice(0, visibleMessages).map((message, index) => (
            <div
              key={index}
              className="flex justify-end animate-in slide-in-from-right-5 fade-in duration-500"
              style={{
                animationDelay: `${index * 200}ms`,
                animationFillMode: "both",
              }}
            >
              <div className="bg-n-secondary px-3 pb-2 2xl:py-3 py-2 rounded-2xl rounded-br-md max-w-xs shadow-lg transform hover:scale-105 transition-transform duration-200">
                <div className="text-sm font-medium">{message.text}</div>
                <div className="flex items-center justify-end gap-1 2xl:mt-2 mt-1">
                  <span className="text-xs opacity-75">{message.time}</span>
                  {message.status === "delivered" ? (
                    <CheckCheck className="h-3 w-3 opacity-75" />
                  ) : (
                    <Check className="h-3 w-3 opacity-75" />
                  )}
                </div>
              </div>
            </div>
          ))}

          {visibleMessages === 0 && (
            <div className="text-center text-white/60 text-sm animate-pulse">
              Aguardando mensagens...
            </div>
          )}
        </div>
      </div>
      <motion.div
        className="text-end items-end z-10"
        initial={{ x: 100, opacity: 0 }}
        animate={isInView ? { x: 0, opacity: 1 } : {}}
        transition={{ duration: 0.8, ease: "easeOut", delay: 0.2 }}
      >
        <h1
          className="text-n-secondary 2xl:text-[100px] xl:text-[80px] lg:text-[60px] md:text-[50px]"
          style={{ fontFamily: "'Anton', sans-serif" }}
        >
          para transporte de veículos
        </h1>
      </motion.div>
    </section>
  );
};
